import { PLACEHOLDER_VALUE } from '@/constants';
import { InstrumentRunDataRow } from '@/types';
import Table from '@nautilusbio/nb-design-system/Table';
import { CellClickedEvent, CellDoubleClickedEvent, CellValueChangedEvent, ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../../../styles/components/table.module.css';

import { OrganizationNameCellRenderer } from './OrganizationNameCellRenderer';
import { createEmptyRows } from './reducer';
import { StudyNameCellRenderer } from './StudyNameCellRenderer';
import styles from './styles.module.css';

interface SampleTableProps {
  rowData: InstrumentRunDataRow[];
  onRowDataChange?: (data: InstrumentRunDataRow[]) => void;
}

const SampleTable: FC<SampleTableProps> = ({ rowData, onRowDataChange }) => {
  const { t } = useTranslation(['create', 'general']);
  const gridRef = useRef<AgGridReact<InstrumentRunDataRow>>(null);

  // Local state for row data to enable inline editing
  const [localRowData, setLocalRowData] = useState<InstrumentRunDataRow[]>(() =>
    rowData.length === 0 ? createEmptyRows() : rowData,
  );

  // Determine if pagination is needed (more than 12 rows)
  const usePagination = localRowData.length > 12;

  // Sync localRowData whenever the rowData prop genuinely changes (e.g. resize,
  // CSV upload, draft load). Cell edits never change the rowData prop because
  // SampleManager.state.rowData is only updated via dispatch — so there is no
  // risk of accidentally overwriting in-progress edits here.
  useEffect(() => {
    if (rowData.length === 0) {
      setLocalRowData(createEmptyRows());
      return;
    }
    setLocalRowData(rowData);
  }, [rowData]);

  // Handle cell value changes
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<InstrumentRunDataRow>) => {
      const updatedData = [...localRowData];
      const rowIndex = event.rowIndex;

      if (rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 && event.data) {
        // Create a new object to avoid shared references
        updatedData[rowIndex] = { ...event.data };
        setLocalRowData(updatedData);

        // Notify parent component of changes
        if (onRowDataChange) {
          // Filter out completely empty rows before notifying parent
          // A row is non-empty if it has any field filled (demux_index can be 0, which is valid)
          const nonEmptyRows = updatedData.filter(
            row =>
              row.name || row.study_name || row.org_name || row.well_position || typeof row.demux_index === 'number',
          );

          onRowDataChange(nonEmptyRows);
        }
      }
    },
    [localRowData, onRowDataChange],
  );

  // Column Definitions with editable configuration
  const colDefs = useMemo<ColDef<InstrumentRunDataRow>[]>(
    () => [
      {
        field: 'name',
        headerName: t('general:name'),
        editable: _params => {
          // Always editable, but single-click only for empty rows
          return true;
        },
        cellEditor: 'agTextCellEditor',
        cellEditorParams: {
          maxLength: 100,
        },
      },
      {
        field: 'org_name',
        headerName: t('create:run.orgName'),
        cellRenderer: OrganizationNameCellRenderer,
        editable: false, // Editing is handled by the custom cell renderer
      },
      {
        field: 'study_name',
        headerName: t('create:run.studyName'),
        cellRenderer: StudyNameCellRenderer,
        editable: false, // Editing is handled by the custom cell renderer
      },
      {
        cellClass: tableStyles.tableCellCenter,
        field: 'well_position',
        headerName: t('create:run.wellPosition'),
        editable: _params => {
          // Always editable, but single-click only for empty rows
          return true;
        },
        cellEditor: 'agTextCellEditor',
        cellEditorParams: {
          maxLength: 2,
        },
        width: 80,
      },
      {
        cellClass: tableStyles.tableCellCenter,
        field: 'demux_index',
        headerName: t('create:run.demuxIndex'),
        editable: _params => {
          // Always editable, but single-click only for empty rows
          return true;
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 0,
        },
        valueParser: params => {
          const value = params.newValue;
          return value === '' || value === null ? 0 : Number(value);
        },
        width: 80,
      },
    ],
    [t],
  );

  // Check if a row is empty (no user data)
  // Empty rows have placeholder values (e.g. "Sample 1", "A1", PLACEHOLDER_VALUE for strings and 0 for demux_index)
  const isRowEmpty = useCallback((data: InstrumentRunDataRow) => {
    const nameIsDefaultOrEmpty = !data.name || /^Sample\s+\d+$/i.test(data.name);
    const studyIsEmpty = !data.study_name || data.study_name === PLACEHOLDER_VALUE;
    const orgIsEmpty = !data.org_name || data.org_name === PLACEHOLDER_VALUE;
    const wellPositionIsEmpty = !data.well_position || data.well_position === PLACEHOLDER_VALUE;
    const demuxIsEmpty = !data.demux_index || data.demux_index === 0;

    return nameIsDefaultOrEmpty && studyIsEmpty && orgIsEmpty && wellPositionIsEmpty && demuxIsEmpty;
  }, []);

  // Default column definition for all columns
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    [],
  );

  // Handle cell clicks to enable single-click editing for empty rows
  const onCellClicked = useCallback(
    (event: CellClickedEvent<InstrumentRunDataRow>) => {
      // Only enable single-click edit if the row is empty
      if (
        event.data &&
        isRowEmpty(event.data) &&
        event.rowIndex !== null &&
        event.rowIndex !== undefined &&
        event.column
      ) {
        const colId = event.column.getColId();
        event.api.startEditingCell({
          rowIndex: event.rowIndex,
          colKey: colId,
        });
      }
    },
    [isRowEmpty],
  );

  // Handle double-click for all cells (empty or populated)
  const onCellDoubleClicked = useCallback((event: CellDoubleClickedEvent<InstrumentRunDataRow>) => {
    if (event.rowIndex !== null && event.rowIndex !== undefined && event.column) {
      const colId = event.column.getColId();
      event.api.startEditingCell({
        rowIndex: event.rowIndex,
        colKey: colId,
      });
    }
  }, []);

  return (
    <Table
      className={styles.sampleTable}
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      pagination={usePagination}
      paginationPageSize={12}
      paginationPageSizeSelector={false}
      rowData={localRowData}
      suppressContextMenu={true}
      onCellValueChanged={onCellValueChanged}
      onCellClicked={onCellClicked}
      onCellDoubleClicked={onCellDoubleClicked}
      ref={gridRef}
      stopEditingWhenCellsLoseFocus={true}
      tableId="sampleTable"
      suppressClickEdit={true}
      undoRedoCellEditing={true}
      undoRedoCellEditingLimit={20}
    />
  );
};

export default SampleTable;
